import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, tap, throwError, interval, Subscription } from 'rxjs';
import { StorageService } from './storage.service';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import * as signalR from '@microsoft/signalr';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { AuthApiService } from './auth-api.service';
import { UserApiService } from './user-api.service';
import { CrewSelfService } from './crew-self.service';
import { ErrorModalComponent } from 'src/app/auth/error-modal/error-modal.component';
import { distinctUntilChanged } from 'rxjs/operators';
import { BatteryOptimization } from '@capawesome-team/capacitor-android-battery-optimization';
import { AppConfig } from 'src/app/app.config';
import { jwtDecode } from 'jwt-decode';
import { StateData } from 'src/app/auth/auth.component';
import { LoginTrackingService, LOGIN_STAGE } from './login-tracking.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isNative: boolean = Capacitor.isNativePlatform();
  public srHubconnection: signalR.HubConnection | undefined;
  public isLoginInProgress: boolean = false;

  public isOnlineSubject = new BehaviorSubject<boolean>(true); // Default online
  isOnline$ = this.isOnlineSubject.asObservable().pipe(distinctUntilChanged()); // Observable to subscribe to

  private signalRToken: string = '';
  public b2cCodeVerifier: string = '';
  public b2cAuthCodeBySignalR: string = '';
  public b2cLoginStateBySignalR: string = '';

  private refreshTokenInProgress = false;
  public browserFinished: boolean = false;
  public lastUrl = '';
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private connectionTimeSubscription: Subscription | undefined;
   private appActiveSubject = new BehaviorSubject<boolean>(true);
  appActive$ = this.appActiveSubject.asObservable();

  // Tracks whether we registered the browserFinished listener
  private browserListenerRegistered = false;

  constructor(
    private readonly router: Router,
    private readonly authApiService: AuthApiService,
    private readonly _storageService: StorageService,
    private readonly userApiService: UserApiService,
    private readonly crewSelfService: CrewSelfService,
    private readonly modalController: ModalController,
    private readonly loginTracking: LoginTrackingService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    console.log('AuthService constructor initialized');
    this.handleBrowserFinished()
  }

  handleBrowserFinished = () => {
    Browser.addListener('browserFinished', () => {
      if (this.isAppActive) {
        const encAccessToken: any = this._storageService.getDataFromStorage('access_token');
        const decrypted = encAccessToken ? this.crewSelfService.decryptData(encAccessToken) : {};
        if (!decrypted?.access_token && !this.browserFinished) {
          if (this.lastUrl) {
            Browser.open({ url: this.lastUrl });
          } else {
            this.signIn();
          }
        } else if (this.lastUrl) {
          Browser.open({ url: this.lastUrl });
        }
      }
    });
  };

   setAppActiveState(isActive: boolean) {
    console.log('AuthService: setAppActiveState =', isActive);
    this.appActiveSubject.next(isActive);
  }

  get isAppActive(): boolean {
    return this.appActiveSubject.value;
  }


  async acquireB2CAccessToken(code: string) {
    console.log('acquireB2CAccessToken called with code:', code);
    const enc_code_verifier = this.b2cCodeVerifier || this._storageService.getDataFromStorage('code_verifier');
    console.log('Encrypted code verifier:', enc_code_verifier);
    if (!code || !enc_code_verifier) {
      console.error('Missing code or code verifier');
      // this.showLoginErrorModal();

    } else {
      const code_verifier = this.b2cCodeVerifier ? enc_code_verifier : this.crewSelfService.decryptData(enc_code_verifier)?.['code_verifier'];
      console.log('Decrypted code verifier:', code_verifier);

      this.loginTracking.track(LOGIN_STAGE.TOKEN_EXCHANGE_INIT, 'Exchanging auth code for access token');
      this.isOnlineSubject.value && this.authApiService.b2cTokenApi(code, code_verifier).subscribe({
        next: (response: any) => {
          console.log('b2cTokenApi response:', response);
          this.b2cCodeVerifier = '';
          this._storageService.removeDataFromStorage('code_verifier');
          this.b2cAuthCodeBySignalR = '';
          this.isLoginInProgress = false;

          if (!this.validateB2CToken(response.access_token)) {
            console.error('Invalid user token detected, signing out');
            this.loginTracking.track(LOGIN_STAGE.TOKEN_EXCHANGE_FAILED, 'Token validation failed');
            this.signOut();
          } else {
            this.loginTracking.track(LOGIN_STAGE.TOKEN_EXCHANGE_SUCCESS, 'Access token acquired');
            this._storageService.putDataInStorage(
              'access_token',
              this.crewSelfService.encryptData({ access_token: response.access_token })
            );
            this._storageService.putDataInStorage(
              'refresh_token',
              this.crewSelfService.encryptData({ refresh_token: response.refresh_token })
            );
            console.log('Tokens saved, loading user details');
            this.getApprovedUserDetail();
            // this.getCrewBasicNotifications();
            // this.router.navigate(['home']);

          }
        },
        error: (err: any) => {
          console.error('Error fetching access_token:', err);
          this.loginTracking.track(LOGIN_STAGE.TOKEN_EXCHANGE_FAILED, err?.message || 'Token exchange failed');
          this.showLoginErrorModal();
        },
      });
    }
  }

  public getCrewBasicNotifications() {
    console.log('Fetching crew basic notifications');
    this.userApiService.getCrewBasicNotifications().subscribe({
      next: (response) => {
        console.log('getCrewBasicNotifications response:', response);
        if (response.data.IS_UPDATED) {
          this.crewSelfService.isBasicNotificationSet(response.data);
        }
      },
      error: (err: any) => {
        console.error('Error fetching default notifications:', err);
      }
    });
  }

  public getApprovedUserDetail() {
    console.log('Fetching approved user details');
    this.userApiService.fetchUserApprovedDetail()
      .subscribe({
        next: (response) => {
          console.log('fetchUserApprovedDetail response:', response);
          if (response.status === 'success') {
            this.loginTracking.track(LOGIN_STAGE.LOGIN_SUCCESS, 'User profile loaded successfully');
            this.getCrewBasicNotifications();
            this.router.navigate(['home']);
            this.crewSelfService.isFillipinoSet(response.data.POOL_CODE);
            this.crewSelfService.isPoolCodeSet(response.data.POOL_CODE);
            this.crewSelfService.isSetActiveUser(response.data);
          }
        },
        error: (err: any) => {
          console.error('Error user approved detail:', err);
          this.loginTracking.track(LOGIN_STAGE.PROFILE_FETCH_FAILED, err?.message || 'Failed to fetch user profile');
        }
      });
  }


  redirectToB2c(code_challange: string, logout?: boolean, access_denied?: boolean) {
    console.log('redirectToB2c called with:', { code_challange, logout });
    this.loginTracking.track(LOGIN_STAGE.SIGNALR_INIT, 'Initiating SignalR negotiation');
    this.authApiService.signalRTokenApi(uuidv4(), { platform: 'native', code_challange, access_denied }).subscribe({
      next: (response: any) => {
        console.log('signalRTokenApi response:', response);
        if (response?.data?.status === "SUCCESS") {
          this.b2cLoginStateBySignalR = response.data.state as string;
          const signaRHubEndpoint = response.data.connection.url;
          this.signalRToken = response.data.connection.accessToken;
          console.log("redirectToB2c details:", {
            b2cLoginStateBySignalR: this.b2cLoginStateBySignalR,
            signaRHubEndpoint,
            signalRToken: this.signalRToken
          });
          if (this.signalRToken && signaRHubEndpoint) {
            this.srHubconnection = new signalR.HubConnectionBuilder()
              .withUrl(signaRHubEndpoint, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
                accessTokenFactory: () => this.signalRToken,
              })
              .configureLogging(signalR.LogLevel.Information)
              .build();

            this.registerSignalRListener();
            this.startSignalRHubConnection(code_challange, logout);
          } else {
            console.error('SignalR Token or Endpoint not found');
            this.showLoginErrorModal();
          }
        } else {
          console.error('Error fetching SignalR token:', JSON.stringify(response));
          this.showLoginErrorModal();
        }
      },
      error: (err: any) => {
        console.error('Error fetching SignalR token:', err);
        this.showLoginErrorModal();
      },
    });
  }

  async startSignalRHubConnection(code_challange: string, logout?: boolean) {
    try {
      await this.srHubconnection?.start();
      console.log('startSignalRHubConnection: connection started');

      this.startConnectionTimer();
      this.browserFinished = false;

      if (logout) {
        const state_data = jwtDecode<StateData>(this.b2cLoginStateBySignalR);
        const access_denied = state_data['access_denied'];
        if (access_denied) {
          this.lastUrl = '';
          this.browserFinished = true;
          Browser.close();
          this.signalRToken = '';
          this.srHubconnection?.stop();
          this.b2cLoginStateBySignalR = '';
          this.connectionTimeSubscription?.unsubscribe();
          this.router.navigate(['access-denied']);
        }
        else {
          const logoutUrl = this.getB2CLogoutUrl(this.b2cLoginStateBySignalR);
          console.log('Opening logout URL:', logoutUrl);
          this.lastUrl = logoutUrl;
          Browser.open({ url: logoutUrl });
        }

      } else {
        const loginUrl = this.getB2CLoginUrl(this.b2cLoginStateBySignalR, code_challange);
        console.log('Opening login URL:', loginUrl);
        this.lastUrl = loginUrl;
        this.loginTracking.track(LOGIN_STAGE.BROWSER_OPENED, 'B2C login browser opened');
        Browser.open({ url: loginUrl });
      }
    } catch (err) {
      console.error('startSignalRHubConnection error:', err);
      setTimeout(() => this.startSignalRHubConnection(code_challange, logout), 5000);
    }
  }

  async startConnectionTimer() {
    console.log('startConnectionTimer called');
    this.connectionTimeSubscription = interval(500).subscribe(async () => {
      if (this.isOnlineSubject.value && this.signalRToken) {
        if (this.isSignalRTokenExpired()) {
          console.log('SR token expired, invoking signIn');
          this.signalRToken = '';
          this.connectionTimeSubscription?.unsubscribe();
          if (!this.browserFinished) {
            this.browserFinished = true;
            console.log('Closing browser due to expired token');
            Browser.close();
          }
          this.signIn();
        } else if (
          this.srHubconnection &&
          this.srHubconnection['_connectionState'] !== 'Connected' &&
          this.srHubconnection['_connectionState'] !== 'Connecting'
        ) {
          console.log('Restarting SignalR connection');
          await this.srHubconnection.start();
        }
      }
    });
  }

  registerSignalRListener() {
    console.log('registerSignalRListener called');
    this.srHubconnection?.on('newAuthCode', (code: string, state: string) => {
      console.log('Received newAuthCode event:', { code, state });
      const statMatched = this.b2cLoginStateBySignalR && state === this.b2cLoginStateBySignalR;
      this.lastUrl = '';
      this.browserFinished = true;
      Browser.close();
      this.signalRToken = '';
      this.srHubconnection?.stop();
      this.b2cLoginStateBySignalR = '';
      this.connectionTimeSubscription?.unsubscribe();
      console.log('SignalR connection stopped and browser closed');

      if (!this.b2cAuthCodeBySignalR && code && statMatched) {
        this.loginTracking.track(LOGIN_STAGE.AUTH_CODE_RECEIVED, 'Auth code received via SignalR');
        this.b2cAuthCodeBySignalR = code;
        this.acquireB2CAccessToken(code);
      } else {
        console.error('Auth code invalid or state mismatch');
        this.loginTracking.track(LOGIN_STAGE.LOGIN_FAILED, 'Auth code invalid or state mismatch');
        this.showLoginErrorModal();
      }
    });
  }


  authGuard() {
    try {
      console.log('AuthService: authGuard called');
      const encAccessToken: any = this._storageService.getDataFromStorage('access_token');
      console.log('Encrypted Access Token:', encAccessToken);

      const decrypted = encAccessToken ? this.crewSelfService.decryptData(encAccessToken) : {};
      console.log('Decrypted Access Token:', decrypted);

      if (!decrypted?.access_token) {
        console.log('AuthService: AuthGuard > failed. Token missing');
        this.signIn();
        return false;
      }

      console.log('AuthService: AuthGuard > validated.');
      return true;
    } catch (err) {
      console.log('AuthService: authGuard token error', err);
      this.signOut();
      return false;
    }
  }


  getB2CLoginUrl(state: string, code_challenge: string) {
    const url = (
      AppConfig.settings?.B2C_LOGIN_AUTHORITY +
      '/oauth2/v2.0/authorize' +
      `?client_id=${AppConfig.settings?.B2C_CLIENT_ID}` +
      `&response_type=code` +
      `&redirect_uri=${AppConfig.settings?.B2C_REDIRECT_URI}` +
      `&response_mode=query` +
      `&scope=${AppConfig.settings?.B2C_USER_LOGIN_SCOPE}` +
      `&code_challenge=${code_challenge}` +
      `&state=${state}` +
      `&code_challenge_method=S256`
    );

    console.log('AuthService: Constructed B2C login URL:', url);
    return url;
  }


  getB2CLogoutUrl(state: string) {
    const url = (
      AppConfig.settings?.B2C_LOGIN_AUTHORITY +
      '/oauth2/v2.0/logout' +
      `?post_logout_redirect_uri=${AppConfig.settings?.B2C_LOGOUT_REDIRECT_URI}` +
      `&state=${state}`
    );

    console.log('AuthService: Constructed B2C logout URL:', url);
    return url;
  }

  signOut(isAccessDenied = false) {
    console.log('AuthService: signOut called. isLoginInProgress:', this.isLoginInProgress);

    if (!this.isLoginInProgress) {
      this._storageService.clearStorage();
      console.log('AuthService: Cleared storage and calling signIn.');
      if(isAccessDenied && !this.isNative){
    this.router.navigate(['access-denied']);
      }
     else{
 this.signIn(true, isAccessDenied);
     }
     
     
    }
  }


  async signIn(logoutFromB2C = false, isAccessDenied = false) {
    console.log("AuthService: signIn called. logoutFromB2C =", logoutFromB2C);

    this.loginTracking.startNewSession();
    this.loginTracking.track(LOGIN_STAGE.LOGIN_INIT, `logoutFromB2C=${logoutFromB2C}`);

    if (Capacitor.getPlatform() === 'android') {
      const isBatteryOptDisabled = await BatteryOptimization.isBatteryOptimizationEnabled();
      console.log("AuthService: Battery optimization check result:", isBatteryOptDisabled);

      if (isBatteryOptDisabled.enabled) {
        console.warn("AuthService: Battery optimization is enabled. Sign-in aborted.");
        return;
      }
    }

    if (!this.isOnlineSubject.value) {
      console.warn("AuthService: No internet connection. Sign-in aborted.");
      return;
    }

    this.generateCodeVerifierAndChallenge()
      .then((response) => {
        console.log("AuthService: PKCE code_verifier & code_challenge generated:", response);
        this.isLoginInProgress = true;

        if (this.isNative) {
          console.log("AuthService: Native platform detected. Redirecting to B2C login.");
          this.b2cCodeVerifier = response.codeVerifier;
          this.router.navigate(['auth']);
          this.redirectToB2c(response.codeChallenge, logoutFromB2C, isAccessDenied);
        } else {
          console.log("AuthService: Web platform detected. Calling webTokenApi.");

          this.authApiService.webTokenApi(uuidv4(), {
            platform: 'web',
            code_challange: response.codeChallenge,
            access_denied: isAccessDenied
          }).subscribe({
            next: (res: any) => {
              console.log("AuthService: Received response from webTokenApi:", res);
              this.loginTracking.track(LOGIN_STAGE.WEB_TOKEN_SUCCESS, 'Web token negotiated');

              this._storageService.putDataInStorage(
                'code_verifier',
                this.crewSelfService.encryptData({ code_verifier: response.codeVerifier })
              );

              const redirectUrl = logoutFromB2C
                ? this.getB2CLogoutUrl(res.state)
                : this.getB2CLoginUrl(res.state, response.codeChallenge);

              console.log("AuthService: Redirecting to B2C URL:", redirectUrl);
              window.location.href = redirectUrl;
            },
            error: (err: any) => {
              console.error("AuthService: Error from webTokenApi:", err);
              this.loginTracking.track(LOGIN_STAGE.WEB_TOKEN_FAILED, err?.message || 'Web token negotiation failed');
              this.showLoginErrorModal();
            },
          });
        }
      })
      .catch((err) => {
        console.error("AuthService: Error generating PKCE challenge:", err);
      });
  }

  async generateCodeVerifierAndChallenge() {
    const codeVerifier = this.generateRandomString(128);
    console.log("AuthService: Generated code_verifier:", codeVerifier);

    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    console.log("AuthService: Generated code_challenge from code_verifier:", codeChallenge);

    return { codeVerifier, codeChallenge };
  }


  // Helper function to generate a random string of a given length  
  generateRandomString(length: number) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let randomString = '';
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      randomString += charset[randomValues[i] % charset.length];
    }

    console.log(`AuthService: Generated random string of length ${length}`);
    return randomString;
  }


  // Helper function to generate a code_challenge from a code_verifier  
  async generateCodeChallenge(codeVerifier: string) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const digest = await window.crypto.subtle.digest('SHA-256', data);

      console.log("AuthService: SHA-256 digest generated for code_verifier.");
      return this.base64UrlEncode(new Uint8Array(digest));
    } catch (error) {
      console.error("AuthService: Failed to generate code_challenge:", error);
      throw error;
    }
  }


  base64UrlEncode(buffer: Uint8Array) {
    let base64 = btoa(String.fromCharCode(...buffer)).replace(/\+/g, '-').replace(/\//g, '_');
    let i = base64.length - 1;
    while (i >= 0 && base64[i] === '=') {
      i--;
    }
    base64 = base64.substring(0, i + 1);
    return base64;
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshTokenInProgress) {
      console.log("AuthService: refreshAccessToken() already in progress.");
      return this.refreshTokenSubject.asObservable() as any;
    } else {
      this.refreshTokenInProgress = true;

      const enc_refresh_token: any = this._storageService.getDataFromStorage('refresh_token');
      console.log("AuthService: Retrieved encrypted refresh token:", enc_refresh_token);

      let refresh_token: string;
      try {
        const decrypted = this.crewSelfService.decryptData(enc_refresh_token);
        refresh_token = decrypted.refresh_token;
        console.log("AuthService: Decrypted refresh_token:", refresh_token);
      } catch (err) {
        console.error("AuthService: Error decrypting refresh token:", err);
        this.refreshTokenInProgress = false;
        return throwError(() => err);
      }

      return this.authApiService.b2cRefreshTokenApi(refresh_token).pipe(
        tap((response: any) => {
          console.log("AuthService: Token refreshed successfully:", response);

          this._storageService.putDataInStorage(
            'access_token',
            this.crewSelfService.encryptData({ access_token: response.access_token })
          );
          this._storageService.putDataInStorage(
            'refresh_token',
            this.crewSelfService.encryptData({ refresh_token: response.refresh_token })
          );

          this.refreshTokenInProgress = false;
        }),
        map(response => response.access_token),
        catchError(err => {
          console.error("AuthService: Error during token refresh:", err);
          this.refreshTokenInProgress = false;
          return throwError(() => err);
        })
      );
    }
  }


  isTokenExpired(): boolean {
    try {
      const enc_token = this._storageService.getDataFromStorage('access_token');
      console.log('AuthService: Encrypted access token from storage:', enc_token);

      if (!enc_token) {
        console.warn('AuthService: No token found, considered expired.');
        return true;
      }

      const { access_token } = this.crewSelfService.decryptData(enc_token);
      console.log('AuthService: Decrypted access token:', access_token);

      const decode = this.decodeJwtPayload(access_token);
      if (!decode) {
        console.warn('AuthService: Failed to decode token, considered expired.');
        return true;
      }

      const expiry = decode.exp;
      const now = Math.floor(new Date().getTime() / 1000);
      console.log(`AuthService: Token expiry = ${expiry}, current time = ${now}`);

      return now >= expiry;
    } catch (err) {
      console.error('AuthService: Error checking token expiration:', err);
      return true;
    }
  }


  async showLoginErrorModal(loginState: string = '') {
    this.isLoginInProgress = false;
    this.loginTracking.track(LOGIN_STAGE.LOGIN_FAILED, loginState || 'Login error modal shown');
    const modal = this.modalController.create({
      component: ErrorModalComponent,
      cssClass: 'delete-modal',
      componentProps: {
        loginState: loginState,
      },
      backdropDismiss: false
    });

    (await modal).present();
  }

  validateB2CToken(token: string): boolean {
    if (!token) {
      console.warn('AuthService: validateB2CToken > token is empty.');
      return false;
    }

    try {
      const tokenInfo = JSON.parse(atob(token.split('.')[1]));
      console.log('AuthService: Decoded B2C token info:', tokenInfo);

      return !!(tokenInfo["signInNames.userName"] && tokenInfo["signInNames.emailAddress"]);
    } catch (err) {
      console.error('AuthService: Error validating B2C token:', err);
      return false;
    }
  }


  isSignalRTokenExpired(): boolean {
    const token = this.signalRToken;
    console.log('AuthService: Checking SignalR token expiration. Token:', token);

    if (!token) {
      console.warn('AuthService: SignalR token not found. Considered expired.');
      return true;
    }

    const decode = this.decodeJwtPayload(token);
    if (!decode) {
      console.warn('AuthService: Failed to decode SignalR token.');
      return true;
    }

    const expiry = decode.exp;
    const now = Math.floor(new Date().getTime() / 1000);
    console.log(`AuthService: SignalR token expiry = ${expiry}, current time = ${now}`);

    return now >= expiry;
  }


  base64UrlDecode(str: string) {
    console.log('AuthService: Decoding base64Url string:', str);

    str = str.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) {
      str += '=';
    }

    const decoded = atob(str);
    console.log('AuthService: Decoded base64 string:', decoded);
    return decoded;
  }

  decodeJwtPayload(token: string) {
    try {
      const base64Payload = token.split('.')[1];
      console.log('AuthService: JWT payload base64 part:', base64Payload);

      const jsonString = this.base64UrlDecode(base64Payload);
      const parsed = JSON.parse(jsonString);
      console.log('AuthService: Parsed JWT payload:', parsed);

      return parsed;
    } catch (e) {
      console.error('AuthService: Invalid token during decodeJwtPayload:', e);
      return null;
    }
  }
}